/**
 * Composition and distribution charts.
 *
 * These attach pointer handlers per mark rather than to a plot-wide overlay, so
 * none of them needs a hook inside ChartShell's render callback.
 */

import { useState } from 'react'
import { ChartShell, seriesColor, seqColor, toneColor, useChartTooltip } from './chart-kit'
import { arcPath, barPath, linearScale, niceDomain, niceTicks } from './scale'

/* ----------------------------------------------------------------- donut -- */

export interface DonutSlice {
  label: string
  value: number
  color?: string
}

export interface DonutProps {
  slices: DonutSlice[]
  height?: number
  format: (value: number) => string
  label: string
  /** Big number in the middle. Pair with `centerLabel`. */
  centerValue?: string
  centerLabel?: string
  emptyMessage?: string
  thickness?: number
}

/**
 * Composition of a whole. Capped at ~7 visible slices by the caller — beyond
 * that a ranked bar chart is strictly more readable, so this component does not
 * try to rescue an over-segmented dataset.
 */
export function Donut({
  slices,
  height = 200,
  format,
  label,
  centerValue,
  centerLabel,
  emptyMessage,
  thickness = 22,
}: DonutProps) {
  const tooltip = useChartTooltip()
  const [active, setActive] = useState<number | null>(null)

  const usable = slices.filter((s) => Number.isFinite(s.value) && s.value > 0)
  const total = usable.reduce((sum, s) => sum + s.value, 0)

  return (
    <ChartShell
      height={height}
      label={label}
      isEmpty={usable.length === 0 || total <= 0}
      empty={emptyMessage}
      margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
    >
      {(plot) => {
        const size = Math.min(plot.inner.width, plot.inner.height)
        const cx = plot.margin.left + plot.inner.width / 2
        const cy = plot.margin.top + plot.inner.height / 2
        const outer = size / 2
        const inner = Math.max(0, outer - thickness)

        let angle = 0
        return (
          <g>
            {usable.map((slice, i) => {
              const sweep = (slice.value / total) * Math.PI * 2
              const start = angle
              angle += sweep
              const color = slice.color ?? seriesColor(i)
              return (
                <path
                  key={slice.label}
                  d={arcPath(cx, cy, outer, inner, start, angle)}
                  fill={color}
                  fillOpacity={active === null || active === i ? 1 : 0.35}
                  stroke="var(--w7-bg-card)"
                  strokeWidth={1.5}
                  onPointerEnter={(event) => {
                    setActive(i)
                    tooltip.show({
                      x: event.clientX,
                      y: event.clientY,
                      title: slice.label,
                      rows: [
                        { label: 'Value', value: format(slice.value), color },
                        { label: 'Share', value: `${((slice.value / total) * 100).toFixed(1)}%` },
                      ],
                    })
                  }}
                  onPointerLeave={() => {
                    setActive(null)
                    tooltip.hide()
                  }}
                />
              )
            })}
            {centerValue ? (
              <>
                <text
                  x={cx}
                  y={cy - (centerLabel ? 5 : 0)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--w7-text)"
                  fontSize={18}
                  fontWeight={650}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {centerValue}
                </text>
                {centerLabel ? (
                  <text
                    x={cx}
                    y={cy + 13}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="var(--w7-text-3)"
                    fontSize={10}
                    letterSpacing="0.08em"
                  >
                    {centerLabel.toUpperCase()}
                  </text>
                ) : null}
              </>
            ) : null}
          </g>
        )
      }}
    </ChartShell>
  )
}

/* ----------------------------------------------------------- ranked bars -- */

export interface RankedRow {
  label: string
  value: number
  /** Optional secondary figure shown at the row end. */
  note?: string
  color?: string
  onClick?: () => void
}

export interface RankedBarsProps {
  rows: RankedRow[]
  format: (value: number) => string
  label: string
  /** Row height in px. */
  rowHeight?: number
  labelWidth?: number
  emptyMessage?: string
  /** Colour each bar by the sign of its value instead of the categorical palette. */
  byTone?: boolean
}

/**
 * A ranked horizontal bar list — the right form for "top N by X" because the
 * labels are readable and the ordering is the message.
 *
 * Rendered as HTML rather than SVG so the labels wrap, truncate and stay
 * selectable, and so each row can be a real button.
 */
export function RankedBars({
  rows,
  format,
  label,
  rowHeight = 30,
  labelWidth = 132,
  emptyMessage = 'Nothing to rank yet',
  byTone = false,
}: RankedBarsProps) {
  if (rows.length === 0) {
    return (
      <div className="w7-state" style={{ padding: 'var(--w7-s5) 0', fontSize: 13 }}>
        {emptyMessage}
      </div>
    )
  }

  const extent = Math.max(...rows.map((r) => Math.abs(r.value)), 1)
  const hasNegative = rows.some((r) => r.value < 0)

  return (
    <ul className="w7-ranked" aria-label={label}>
      {rows.map((row, i) => {
        const magnitude = Math.abs(row.value) / extent
        const color = byTone ? toneColor(row.value) : (row.color ?? seriesColor(i))
        const Tag = row.onClick ? 'button' : 'div'
        return (
          <li key={`${row.label}-${i}`} style={{ height: rowHeight }}>
            <Tag
              className="w7-ranked-row"
              onClick={row.onClick}
              type={row.onClick ? 'button' : undefined}
            >
              <span className="w7-ranked-label w7-truncate" style={{ width: labelWidth }} title={row.label}>
                {row.label}
              </span>
              <span className="w7-ranked-track">
                {hasNegative ? <span className="w7-ranked-zero" /> : null}
                <span
                  className="w7-ranked-fill"
                  style={{
                    width: `${magnitude * (hasNegative ? 50 : 100)}%`,
                    background: color,
                    left: hasNegative ? (row.value < 0 ? undefined : '50%') : 0,
                    right: hasNegative && row.value < 0 ? '50%' : undefined,
                  }}
                />
              </span>
              <span className="w7-ranked-value w7-mono">{format(row.value)}</span>
              {row.note ? <span className="w7-ranked-note">{row.note}</span> : null}
            </Tag>
          </li>
        )
      })}
    </ul>
  )
}

/* --------------------------------------------------------------- heatmap -- */

export interface HeatmapProps {
  /** Row-major values; `rows.length` bands down, `columns.length` across. */
  matrix: number[][]
  rows: string[]
  columns: string[]
  format: (value: number) => string
  label: string
  height?: number
  emptyMessage?: string
  /** Label every nth column. */
  columnStride?: number
}

/** Density over two categorical axes — when a downline actually bets. */
export function Heatmap({
  matrix,
  rows,
  columns,
  format,
  label,
  height = 190,
  emptyMessage,
  columnStride = 3,
}: HeatmapProps) {
  const tooltip = useChartTooltip()
  const flat = matrix.flat()
  const max = Math.max(0, ...flat)

  return (
    <ChartShell
      height={height}
      label={label}
      isEmpty={max <= 0}
      empty={emptyMessage}
      margin={{ top: 4, right: 4, bottom: 18, left: 34 }}
    >
      {(plot) => {
        const cellW = plot.inner.width / Math.max(1, columns.length)
        const cellH = plot.inner.height / Math.max(1, rows.length)
        const gap = cellW > 8 && cellH > 8 ? 1.5 : 0.5

        return (
          <g>
            {rows.map((rowLabel, r) => (
              <text
                key={rowLabel}
                x={plot.margin.left - 7}
                y={plot.margin.top + r * cellH + cellH / 2}
                textAnchor="end"
                dominantBaseline="middle"
                fill="var(--w7-axis)"
                fontSize={10}
              >
                {rowLabel}
              </text>
            ))}
            {columns.map((colLabel, c) =>
              c % columnStride === 0 ? (
                <text
                  key={colLabel + c}
                  x={plot.margin.left + c * cellW + cellW / 2}
                  y={plot.margin.top + plot.inner.height + 12}
                  textAnchor="middle"
                  fill="var(--w7-axis)"
                  fontSize={10}
                >
                  {colLabel}
                </text>
              ) : null,
            )}
            {matrix.map((row, r) =>
              row.map((value, c) => (
                <rect
                  key={`${r}-${c}`}
                  x={plot.margin.left + c * cellW + gap / 2}
                  y={plot.margin.top + r * cellH + gap / 2}
                  width={Math.max(1, cellW - gap)}
                  height={Math.max(1, cellH - gap)}
                  rx={2}
                  fill={seqColor(max > 0 ? value / max : 0)}
                  onPointerEnter={(event) =>
                    tooltip.show({
                      x: event.clientX,
                      y: event.clientY,
                      title: `${rows[r] ?? ''} · ${columns[c] ?? ''}`,
                      rows: [{ label: 'Value', value: format(value) }],
                    })
                  }
                  onPointerLeave={tooltip.hide}
                />
              )),
            )}
          </g>
        )
      }}
    </ChartShell>
  )
}

/* ------------------------------------------------------------- waterfall -- */

export interface WaterfallStep {
  label: string
  value: number
  /** A total bar sits on the baseline instead of floating. */
  isTotal?: boolean
  color?: string
}

export interface WaterfallProps {
  steps: WaterfallStep[]
  format: (value: number) => string
  label: string
  height?: number
  emptyMessage?: string
}

/**
 * How one amount decomposes into parts — used for the hierarchy P&L split,
 * where a member's loss fans out across five agents' share percentages.
 *
 * A waterfall is the only form that shows both each slice AND the running
 * remainder, which is the actual question ("what reaches me after the levels
 * below take theirs?").
 */
export function Waterfall({ steps, format, label, height = 210, emptyMessage }: WaterfallProps) {
  const tooltip = useChartTooltip()

  const cumulative: { start: number; end: number }[] = []
  let running = 0
  for (const step of steps) {
    if (step.isTotal) {
      cumulative.push({ start: 0, end: step.value })
    } else {
      cumulative.push({ start: running, end: running + step.value })
      running += step.value
    }
  }

  const bounds = cumulative.flatMap((c) => [c.start, c.end])

  return (
    <ChartShell
      height={height}
      label={label}
      isEmpty={steps.length === 0}
      empty={emptyMessage}
      margin={{ top: 8, right: 8, bottom: 34, left: 56 }}
    >
      {(plot) => {
        const domain = niceDomain(bounds, { includeZero: true })
        const y = linearScale(domain, [plot.inner.height, 0])
        const step = plot.inner.width / Math.max(1, steps.length)
        const barW = Math.min(48, step * 0.62)

        return (
          <g>
            {niceTicks(domain[0], domain[1], 4).map((t) => (
              <g key={t}>
                <line
                  x1={plot.margin.left}
                  x2={plot.margin.left + plot.inner.width}
                  y1={plot.margin.top + y(t)}
                  y2={plot.margin.top + y(t)}
                  stroke={t === 0 ? 'var(--w7-zero-line)' : 'var(--w7-grid)'}
                  shapeRendering="crispEdges"
                />
                <text
                  x={plot.margin.left - 8}
                  y={plot.margin.top + y(t)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="var(--w7-axis)"
                  fontSize={10}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {format(t)}
                </text>
              </g>
            ))}

            {steps.map((s, i) => {
              const band = cumulative[i]!
              const top = Math.min(y(band.start), y(band.end))
              const h = Math.max(2, Math.abs(y(band.end) - y(band.start)))
              const x = plot.margin.left + i * step + (step - barW) / 2
              const color = s.color ?? (s.isTotal ? 'var(--w7-c1)' : toneColor(s.value))
              return (
                <g key={`${s.label}-${i}`}>
                  {i > 0 && !s.isTotal ? (
                    <line
                      x1={plot.margin.left + (i - 1) * step + (step + barW) / 2}
                      x2={x}
                      y1={plot.margin.top + y(band.start)}
                      y2={plot.margin.top + y(band.start)}
                      stroke="var(--w7-border-strong)"
                      strokeDasharray="3 3"
                    />
                  ) : null}
                  <path
                    d={barPath(x, plot.margin.top + top, barW, h, [3, 3, 3, 3])}
                    fill={color}
                    fillOpacity={s.isTotal ? 0.95 : 0.8}
                    onPointerEnter={(event) =>
                      tooltip.show({
                        x: event.clientX,
                        y: event.clientY,
                        title: s.label,
                        rows: [{ label: s.isTotal ? 'Total' : 'Share', value: format(s.value), color }],
                      })
                    }
                    onPointerLeave={tooltip.hide}
                  />
                  <text
                    x={x + barW / 2}
                    y={plot.margin.top + plot.inner.height + 14}
                    textAnchor="middle"
                    fill="var(--w7-axis)"
                    fontSize={10}
                  >
                    {s.label}
                  </text>
                  <text
                    x={x + barW / 2}
                    y={plot.margin.top + plot.inner.height + 26}
                    textAnchor="middle"
                    fill="var(--w7-text-2)"
                    fontSize={10}
                    fontWeight={600}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {format(s.value)}
                  </text>
                </g>
              )
            })}
          </g>
        )
      }}
    </ChartShell>
  )
}

/* ----------------------------------------------------------- book ladder -- */

export interface BookPosition {
  name: string
  net: number
}

export interface BookLadderProps {
  positions: BookPosition[]
  format: (value: number) => string
  label: string
  emptyMessage?: string
  /** Highlight the worst outcome — the number that actually gates credit. */
  highlightWorst?: boolean
  rowHeight?: number
}

/**
 * The exchange book, one row per selection, diverging around zero.
 *
 * This is the single most important visual in the console and it did not exist
 * in the product being replaced — that showed a flat table of zeroes with no
 * indication of which outcome hurt. Here the worst case is impossible to miss:
 * it is the longest red bar and it is labelled.
 */
export function BookLadder({
  positions,
  format,
  label,
  emptyMessage = 'No open positions in this market',
  highlightWorst = true,
  rowHeight = 34,
}: BookLadderProps) {
  if (positions.length === 0) {
    return (
      <div className="w7-state" style={{ padding: 'var(--w7-s5) 0', fontSize: 13 }}>
        {emptyMessage}
      </div>
    )
  }

  const extent = Math.max(...positions.map((p) => Math.abs(p.net)), 1)
  const worst = Math.min(...positions.map((p) => p.net))

  return (
    <ul className="w7-ladder" aria-label={label}>
      {positions.map((position) => {
        const isWorst = highlightWorst && position.net === worst && worst < 0
        const magnitude = (Math.abs(position.net) / extent) * 50
        return (
          <li key={position.name} style={{ height: rowHeight }} data-worst={isWorst ? 'true' : undefined}>
            <span className="w7-ladder-name w7-truncate" title={position.name}>
              {position.name}
            </span>
            <span className="w7-ladder-track">
              <span className="w7-ladder-zero" />
              <span
                className="w7-ladder-fill"
                style={{
                  width: `${magnitude}%`,
                  background: toneColor(position.net),
                  left: position.net >= 0 ? '50%' : undefined,
                  right: position.net < 0 ? '50%' : undefined,
                }}
              />
            </span>
            <span
              className={`w7-ladder-value w7-mono ${position.net > 0 ? 'w7-tone-profit' : position.net < 0 ? 'w7-tone-loss' : 'w7-tone-neutral'}`}
            >
              {format(position.net)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------------- gauge arc -- */

export interface GaugeProps {
  /** 0..1. Values above 1 render as an over-limit state. */
  value: number
  label: string
  centerValue: string
  centerLabel?: string
  height?: number
}

/** Limit usage as a 270° arc — reads as "how close to the edge" at a glance. */
export function Gauge({ value, label, centerValue, centerLabel, height = 150 }: GaugeProps) {
  const ratio = Number.isFinite(value) ? Math.max(0, value) : 0
  const shown = Math.min(1, ratio)
  const sweep = Math.PI * 1.5
  const start = -sweep / 2
  const color = ratio > 1 ? 'var(--w7-loss)' : ratio > 0.8 ? 'var(--w7-warn)' : 'var(--w7-profit)'

  return (
    <ChartShell
      height={height}
      label={label}
      margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
    >
      {(plot) => {
        const size = Math.min(plot.inner.width, plot.inner.height * 1.25)
        const cx = plot.margin.left + plot.inner.width / 2
        const cy = plot.margin.top + plot.inner.height / 2 + size * 0.08
        const outer = size / 2
        const inner = outer - Math.max(9, size * 0.11)

        return (
          <g>
            <path
              d={arcPath(cx, cy, outer, inner, start, start + sweep)}
              fill="var(--w7-bg-raised)"
            />
            <path
              d={arcPath(cx, cy, outer, inner, start, start + sweep * shown)}
              fill={color}
            />
            <text
              x={cx}
              y={cy - 4}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--w7-text)"
              fontSize={20}
              fontWeight={650}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {centerValue}
            </text>
            {centerLabel ? (
              <text
                x={cx}
                y={cy + 15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--w7-text-3)"
                fontSize={10}
                letterSpacing="0.08em"
              >
                {centerLabel.toUpperCase()}
              </text>
            ) : null}
          </g>
        )
      }}
    </ChartShell>
  )
}
